import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  consentFormStepThunk,
  getDocumentsListThunk,
  getUploadedFileByIdThunk,
  postSaveUploadedFileThunk,
} from "../../stepper/redux/thunk/documentThunk";
import { AppDispatch, RootReducerType, RootState } from "@store/store";
import { StepsButton } from "../../stepper/components/StepsButtons";
import { setActiveStep } from "../../stepper/redux/stepperSlice";
import ConsentDetail from "../../stepper/components/ConsentDetail";
import { toast } from "react-toastify";
import { generateConsentText } from "../../stepper/components/UndertackingContent/generateConsentText";
import { generateLegacyConsentText } from "../utils/generateLegacyConsent";

const LegacyUnderTakingConsent = ({
  collapsed,
  isDetailsPage,
  access,
  isLegacyContent,
  onNext,
  onBack,
  setSubmitHandler,
  setIsConsentValid,
}: {
  collapsed?: boolean;
  isDetailsPage?: boolean;
  access?: any;
  isLegacyContent?: boolean;
  onNext?: () => void;
  onBack?: () => void;
  setSubmitHandler?: (handler: () => Promise<void>) => void;
  setIsConsentValid?: (valid: boolean) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeStep } = useSelector((state: RootReducerType) => state.stepper);
  const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
  const { access_category_id, access_id, ...accessLegacy } = useSelector(
    (state: RootReducerType) => state.accessLegacy,
  );
  const { profile, loading, error } = useSelector(
    (state: RootState) => state.profile,
  );

  const [isApplicationDetailsExpanded, setIsApplicationDetailsExpanded] =
    React.useState<{ [key: number]: boolean }>({});

  const category_id = Number(access_category_id) || 0;

  // API fetch & merge, includes static content after fetch
  const fetchAndMergeDocuments = async () => {
    const documentListData = await dispatch(
      getDocumentsListThunk({
        category_id: category_id,
        access_id: (access_id || access) as number,
        document: "undertaking",
      }),
    );
    const baseDocs = documentListData.payload.data || [];
    const uploadedFileById = await dispatch(
      getUploadedFileByIdThunk({
        access_id: (access_id || access) as number,
        category_id: category_id,
        document: "undertaking",
      }),
    );
    const uploadedFiles = uploadedFileById.payload.data?.files || [];
    const mergedDocs = baseDocs.map((doc: any) => {
      const uploadedFile =
        uploadedFiles.length > 0 &&
        uploadedFiles.find((file: any) => file.document_id === doc.document_id);
      return uploadedFile ? { ...doc, uploadedFile } : doc;
    });
    const uploadedFileIds = new Set(
      uploadedFiles?.map((file: any) => file.document_id),
    );
    const everyDocUploaded = baseDocs.every((doc: any) =>
      uploadedFileIds.has(doc.document_id),
    );
    setAllDocuments(mergedDocs);

    // Auto-expand first document on initial load
    if (
      baseDocs.length > 0 &&
      Object.keys(isApplicationDetailsExpanded).length === 0
    ) {
      const firstDoc = baseDocs.find(
        (doc: { document_type: string }) => doc.document_type === "UNDERTAKING",
      );
      if (firstDoc) {
        setIsApplicationDetailsExpanded({ [firstDoc.document_id]: true });
      }
    }
  };

  React.useEffect(() => {
    fetchAndMergeDocuments();
  }, [dispatch, category_id, access_id]);

  const undertakingDocs = allDocuments.filter(
    (doc) => doc.document_type === "UNDERTAKING",
  );

  // Toggle single section
  const toggleExpand = (document_id: number) => {
    setIsApplicationDetailsExpanded((prev) => ({
      ...prev,
      [document_id]: !prev[document_id],
    }));
  };

  // Expand/collapse all
  const toggleExpandAllSections = (expand: boolean) => {
    const all = {} as { [key: number]: boolean };
    undertakingDocs.forEach((doc) => {
      all[doc.document_id] = expand;
    });
    setIsApplicationDetailsExpanded(all);
  };

  // Consent checkbox state
  const [consentStates, setConsentStates] = useState<{
    [key: number]: boolean;
  }>({});

  const handleConsent = async (document_id: number) => {
    // Generate dynamic consent text
    const consentText = generateLegacyConsentText(
      document_id,
      profile,
      accessLegacy,
    );

    const res = await dispatch(
      postSaveUploadedFileThunk({
        application_id: (access_id || access) as number,
        document_id: document_id,
        isUndertaking: true,
        moduletype: "access",
        consent_text: consentText, // Add dynamic consent text to payload
      }),
    );

    if (res?.meta?.requestStatus === "fulfilled") {
      fetchAndMergeDocuments();

      // Update consent state
      setConsentStates((prev) => ({
        ...prev,
        [document_id]: true,
      }));

      // Close current accordion and open next one
      const currentIndex = undertakingDocs.findIndex(
        (doc) => doc.document_id === document_id,
      );
      if (currentIndex !== -1 && currentIndex < undertakingDocs.length - 1) {
        const nextDoc = undertakingDocs[currentIndex + 1];
        setIsApplicationDetailsExpanded((prev) => ({
          ...prev,
          [document_id]: false, // Close current
          [nextDoc.document_id]: true, // Open next
        }));
      } else if (currentIndex === undertakingDocs.length - 1) {
        // Last document - just close the current one
        setIsApplicationDetailsExpanded((prev) => ({
          ...prev,
          [document_id]: false,
        }));
      }
      toast.success("Undertakings & Consents saved successfully");
    } else {
      console.error("Failed to save undertaking document:");
      toast.error("Failed to save undertaking. Please try again.");
    }
  };

  const handleConsentChange = (docId: number) => {
    handleConsent(docId);
  };

  const allCollapsed =
    undertakingDocs.length > 0
      ? Object.values(isApplicationDetailsExpanded).every((v) => !v)
      : true;

  const allConsentedCount = Object.values(consentStates).filter(Boolean).length;

  const backButtonClickHandler = () => {
    if (!isLegacyContent) {
      dispatch(setActiveStep(activeStep - 1));
    }
  };

  const handleSubmit = async () => {
    try {
      const res = await dispatch(
        consentFormStepThunk({ access_id: access_id as number }),
      );
      dispatch(setActiveStep(activeStep + 1));
    } catch (error) {
      console.error("Error submitting consent form:", error);
      toast.error("Failed to submit form. Please try again.");
    }
  };

  React.useEffect(() => {
    if (setIsConsentValid) {
      setIsConsentValid(
        undertakingDocs.length > 0 &&
          allConsentedCount === undertakingDocs.length,
      );
    }
  }, [allConsentedCount, undertakingDocs.length, setIsConsentValid]);

  return (
    <Box className="amenities-container" component="div">
      <Box
        sx={{
          display: "flex",
        }}
      >
        <h3 className="stepper-hd w-full body-l flex items-center justify-between">
          Undertakings & Consents
        </h3>
        <Box
          className="app-expand-buttons"
          sx={{ borderBottom: "1px solid #cfdae7" }}
        >
          {allCollapsed ? (
            <Button
              startIcon={<UnfoldMoreIcon className="rotate-45 brand-900" />}
              onClick={() => toggleExpandAllSections(true)}
            >
              <Typography
                className="brand-900"
                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Expand All
              </Typography>
            </Button>
          ) : (
            <Button
              startIcon={<UnfoldLessIcon className="rotate-45 brand-900" />}
              onClick={() => toggleExpandAllSections(false)}
            >
              <Typography
                className="brand-900"
                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Collapse All
              </Typography>
            </Button>
          )}
        </Box>
      </Box>

      <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
        <div className="flex items-end justify-between w-full">
          {!isDetailsPage && (
            <div>
              <h5 className="body-m gray-700 font-medium mt-3 mb-4">
                To continue, please review and give your consent for all
                undertakings below.
              </h5>
              <h5 className="body-m gray-900 font-medium">
                Consented: {allConsentedCount} of {undertakingDocs.length}
              </h5>
            </div>
          )}
        </div>
        <Box mt={2}>
          {undertakingDocs.map((doc) => (
            <Card
              key={doc.document_id}
              sx={{ mb: 2, boxShadow: "none", border: "1px solid #E7E8E9" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                className="py-2 bg-gray-100 px-4 m-0 flex items-center justify-between cursor-pointer"
                // onClick={() => toggleExpand(doc.document_id)}
              >
                <h4 className="body-m gray-900 font-medium">
                  {doc.document_name}
                </h4>
                <IconButton
                  aria-label="Collapse section"
                  sx={{ bgcolor: "transparent", p: 0, height: 28, width: 28 }}
                  onClick={() => toggleExpand(doc.document_id)}
                >
                  {isApplicationDetailsExpanded[doc.document_id] ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              </Box>
              {isApplicationDetailsExpanded[doc.document_id] && (
                <Box className="p-4">
                  <ConsentDetail document_id={doc.document_id} />
                  {!isDetailsPage && (
                    <Box className="px-4 py-1 mb-1" sx={{ bgcolor: "#F4F2F7" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={!!consentStates[doc.document_id]}
                            checked={!!consentStates[doc.document_id]}
                            onChange={() =>
                              handleConsentChange(doc.document_id)
                            }
                            sx={{ "&.Mui-checked": { color: "#3A1C73" } }}
                          />
                        }
                        label="I have read and understood the above undertaking and give my consent to its terms."
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "14px",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          ))}
        </Box>
      </Box>
      {!isDetailsPage && !isLegacyContent && (
        <Box className="stepper-btns gray:br-t-500 mt-3">
          <StepsButton
            onBack={backButtonClickHandler}
            onNext={handleSubmit}
            nextDisabled={allConsentedCount !== undertakingDocs.length}
          />
        </Box>
      )}
    </Box>
  );
};

export default LegacyUnderTakingConsent;
